import { mergeCompletedToolActivities } from "../message/mcp-chat-tool-activity-state";
import type { McpChatMessage } from "./mcp-chat-types";

/** Fields a turn's finalize step writes onto its assistant message. */
export type AssistantMessagePatch = Omit<Partial<McpChatMessage>, "id" | "role" | "timestamp">;

export interface UpsertAssistantMessageResult {
  messages: McpChatMessage[];
  /** Id of the message the patch landed on, or undefined when nothing was written. */
  messageId?: string;
}

/**
 * Does this patch carry anything the user can actually SEE?
 *
 * `chatRequestId` and `responseDurationMs` are bookkeeping that finalize()
 * always supplies, so they must never count: treating them as substance made
 * the create-guard unfireable and minted an empty-content assistant message on
 * every turn that produced no text. An empty message hides its bubble row but
 * still draws the wrapper and avatar, so the user saw a bare avatar chip with
 * nothing beside it — the KOA-17572 symptom.
 */
export function patchHasVisibleSubstance(patch: AssistantMessagePatch): boolean {
  return (
    (typeof patch.content === "string" && patch.content.trim().length > 0) ||
    !!patch.completedToolActivities?.length ||
    !!patch.changesSummary ||
    !!patch.structuralDiff ||
    !!patch.suggestions?.length
  );
}

function applyPatchAt(
  messages: McpChatMessage[],
  index: number,
  patch: AssistantMessagePatch
): UpsertAssistantMessageResult {
  const existing: McpChatMessage = messages[index];
  messages[index] = {
    ...existing,
    ...patch,
    ...(patch.completedToolActivities
      ? {
          completedToolActivities: mergeCompletedToolActivities(
            existing.completedToolActivities,
            patch.completedToolActivities
          )
        }
      : {})
  };
  return { messages, messageId: existing.id };
}

/**
 * Fold a finished turn's result into the conversation.
 *
 * Order of preference:
 *  1. Patch the turn's own assistant bubble (the last non-error assistant
 *     message after the last user message). Error bubbles are skipped so a real
 *     answer never merges into one.
 *  2. If the patch has visible substance and there is no such bubble, append a
 *     new assistant message.
 *  3. Otherwise the turn produced nothing to show. Stamp the bookkeeping onto
 *     the error bubble that already explains the outcome — keeping the rating
 *     and telemetry linkage — instead of appending an empty sibling beside it.
 *     The message footer is gated on `!isError`, so this stays invisible.
 */
export function upsertAssistantMessage(
  messages: McpChatMessage[],
  patch: AssistantMessagePatch,
  createMessageId: () => string
): UpsertAssistantMessageResult {
  const nextMessages: McpChatMessage[] = [...messages];

  let lastUserIndex: number = -1;
  for (let i = nextMessages.length - 1; i >= 0; i--) {
    if (nextMessages[i].role === "user") {
      lastUserIndex = i;
      break;
    }
  }

  for (let i = nextMessages.length - 1; i > lastUserIndex; i--) {
    if (nextMessages[i].role === "assistant" && !nextMessages[i].isError) {
      return applyPatchAt(nextMessages, i, patch);
    }
  }

  if (patchHasVisibleSubstance(patch)) {
    const messageId: string = createMessageId();
    nextMessages.push({
      id: messageId,
      role: "assistant",
      content: patch.content || "",
      timestamp: Date.now(),
      ...patch
    });
    return { messages: nextMessages, messageId };
  }

  for (let i = nextMessages.length - 1; i > lastUserIndex; i--) {
    if (nextMessages[i].role === "assistant") {
      return applyPatchAt(nextMessages, i, patch);
    }
  }

  return { messages: nextMessages };
}
